import React from "react";
import { PlayerHeadshotImage } from "../../shared/logos";
import { EVENT_STATUS } from "../../Trader/Cricket/decimalEventStatus";

export const BULLION_DEBUG_MARKETS = process.env.NODE_ENV === "development";
export const BULLION_WIN_BURST_SRC = "/assets/bullion-win-2x.png";
export const BULLION_WIN_BURST_FALLBACK_SRC = "/bullion-win-2x.png";
/** Golden bullion bars — rank-juice overlays, lobby chrome, bonus rows. */
export const BULLION_BARS_SRC = "/assets/bullion.png";
/** Inline bonus bars icon on pick rows (My picks + selection board). */
export const BULLION_BONUS_BARS_ICON_SIZE_CLASS = "h-[24px] w-[24px] shrink-0 object-contain";
/** Wait for row reorder slide before golden juice (ms). */
export const BULLION_LB_LAYOUT_SETTLE_MS = 520;
/** Golden celebration after rows settle (ms). */
export const BULLION_LB_RANK_JUICE_MS = 880;
/** Full rank-change cycle: slide then juice. */
export const BULLION_LB_MOTION_TOTAL_MS = BULLION_LB_LAYOUT_SETTLE_MS + BULLION_LB_RANK_JUICE_MS;
/** Framer layout spring — soft slide (matches Squares-style list reorder). */
export const BULLION_LB_ROW_LAYOUT_SPRING = {
    type: "spring" as const,
    stiffness: 130,
    damping: 26,
    mass: 1.05,
};
/** Ease for rank-juice overlays (not layout). */
export const BULLION_LB_JUICE_EASE = [0.22, 1, 0.36, 1] as const;
/**
 * Climbing leaderboard row background — a brighter, even gold than `BULLION_HEADER_GRADIENT`
 * (which fades to dark brown on the right and made the climbing row look darkened).
 */
export const BULLION_LB_CLIMB_BG_GRADIENT =
    "linear-gradient(91deg, #FBF0A8 0%, #FCE9A0 28%, #F4DA8E 58%, #EAC97F 82%, #F3D88B 100%)";
/** Leaderboard row grid: rank | avatar | name+predictions | pot | points | chevron */
export const BULLION_LEADERBOARD_GRID_COLS_CLASS =
    "grid-cols-[20px_38px_minmax(0,1fr)_40px_40px_18px] sm:grid-cols-[20px_38px_minmax(0,1fr)_52px_52px_24px] lg:grid-cols-[20px_38px_minmax(0,1fr)_60px_60px_minmax(24px,32px)]";
/** Rank tile — fixed 20×20 box (white or gold gradient). */
export const BULLION_LEADERBOARD_RANK_BOX_SIZE_CLASS =
    "box-border inline-flex h-[20px] w-[20px] min-h-[20px] min-w-[20px] max-h-[20px] max-w-[20px] shrink-0 items-center justify-center";
export const BULLION_LEADERBOARD_RANK_BOX_CLASS = `${BULLION_LEADERBOARD_RANK_BOX_SIZE_CLASS} text-center font-anton text-[12px] font-normal uppercase leading-[12px] tracking-[0%] text-[#1B062F]`;
/** Leaderboard row avatar — fixed 30×30. */
export const BULLION_LEADERBOARD_AVATAR_SIZE_CLASS =
    "box-border block h-[30px] w-[30px] min-h-[30px] min-w-[30px] max-h-[30px] max-w-[30px] shrink-0";
export const BULLION_LEADERBOARD_AVATAR_INNER_CLASS = `${BULLION_LEADERBOARD_AVATAR_SIZE_CLASS} object-cover`;
export const BULLION_LEADERBOARD_AVATAR_FALLBACK_CLASS = `${BULLION_LEADERBOARD_AVATAR_SIZE_CLASS} flex items-center justify-center bg-[#3a1863] font-anton text-[12px]`;
/** Avatar grid cell — 30×30 image with horizontal breathing room. */
export const BULLION_LEADERBOARD_AVATAR_CELL_CLASS = `relative ${BULLION_LEADERBOARD_AVATAR_SIZE_CLASS} mx-1 justify-self-center`;
/** Leaderboard row player name — Anton 12 / 22px lh, uppercase, white. */
export const BULLION_LEADERBOARD_PLAYER_NAME_CLASS =
    "min-w-0 truncate inline-flex h-[22px] items-center align-middle font-anton text-[12px] font-normal not-italic uppercase leading-[22px] tracking-[0%] [leading-trim:none]";
/** Player column — tighter gap above bar, extra padding below bar within the row. */
export const BULLION_LEADERBOARD_PLAYER_COL_CLASS =
    "flex min-w-0 max-w-full flex-col justify-center gap-1 pl-1 pb-2 text-left sm:pl-2";
export const BULLION_LEADERBOARD_PLAYER_HEADER_ROW_CLASS =
    "flex min-w-0 items-center justify-between gap-2";
/** Leaderboard column header labels — Anton 12 / 100% lh, uppercase, #1B062F on gold bar. */
export const BULLION_LEADERBOARD_HEADER_LABEL_CLASS =
    "font-anton text-[12px] font-normal not-italic uppercase leading-[100%] tracking-[0%] [leading-trim:none]";
export const BULLION_LEADERBOARD_STAT_HEADER_CLASS = `text-center ${BULLION_LEADERBOARD_HEADER_LABEL_CLASS}`;
/** Leaderboard row POT column value — Anton 14 / 20px lh, #32F78D @ 40%. */
export const BULLION_LEADERBOARD_POT_POINTS_CLASS =
    "text-center font-anton text-[14px] font-normal leading-[20px] tracking-[0%] tabular-nums text-[#32F78D]/40";
/** Leaderboard row POINTS column value — Anton 18 / 20px lh, centered, #32F78D. */
export const BULLION_LEADERBOARD_POINTS_CLASS =
    "text-center font-anton text-[18px] font-normal leading-[20px] tracking-[0%] tabular-nums text-[#32F78D]";
export const BULLION_LEADERBOARD_ROW_HEIGHT_CLASS = "h-[54px]";
/** Prediction progress bar segments (won / lost / unsettled track). */
export const BULLION_LB_PRED_CORRECT = "#32F78D";
export const BULLION_LB_PRED_WRONG = "rgba(27, 6, 47, 0.2)";
/** Empty in-play bar shell — neutral track behind segmented fills. */
export const BULLION_LB_PRED_BAR_BG = "rgba(27, 6, 47, 0.2)";
/** Unsettled / potential portion of the in-play prediction bar — #32F78D @ 40%. */
export const BULLION_LB_PRED_POTENTIAL = "rgba(50, 247, 141, 0.4)";
export const BULLION_LB_PRED_BAR_HEIGHT_CLASS = "h-[10px]";
/** Golden segment for settled bonus picks on winning lines. */
export const BULLION_LB_PRED_BONUS_GRADIENT =
    "linear-gradient(107.25deg, #F9FBC7 17.65%, #E6C878 24.64%, #FBF0A8 32.44%, #7B5E2C 72.7%, #AE8E53 82.15%, #AE8E53 89.54%, #7C582D 103.1%)";
/** Leaderboard rank tile when prize coin stacks show (possible / actual winners). */
export const BULLION_LEADERBOARD_PRIZE_RANK_GRADIENT =
    "linear-gradient(152.58deg, #F9FBC7 17.97%, #E6C878 24.74%, #FBF0A8 32.3%, #7B5E2C 71.33%, #AE8E53 80.49%, #AE8E53 87.66%, #7C582D 100.8%)";
/** Prize coin stack — overlaps the left edge of the rank tile (12px coins on 20px box). */
export const BULLION_LEADERBOARD_PRIZE_COIN_STACK_OFFSET_CLASS = "-left-[7px]";
/** BULLION slip / My picks: dark tie mark on purple rows (no white tile). */
export const BULLION_TIE_ICON_SRC = "/assets/tie-dark.svg";
function bullionSlipParseHexRgb(hex: string): { r: number; g: number; b: number } {
    const h = String(hex || "")
        .trim()
        .replace(/^#/, "");
    if (h.length !== 6) return { r: 101, g: 45, b: 175 };
    return {
        r: parseInt(h.slice(0, 2), 16),
        g: parseInt(h.slice(2, 4), 16),
        b: parseInt(h.slice(4, 6), 16),
    };
}

/** My picks market column gradient — single source for panel + headshot tint. */
const BULLION_SLIP_MARKET_GRADIENT_FROM = "#652DAF";
const BULLION_SLIP_MARKET_GRADIENT_TO = "#623998";

export const BULLION_SLIP_MARKET_PANEL_BG_STYLE: React.CSSProperties = {
    backgroundImage: `linear-gradient(180deg, ${BULLION_SLIP_MARKET_GRADIENT_FROM} 0%, ${BULLION_SLIP_MARKET_GRADIENT_TO} 100%)`,
};

/** Gold linear gradient for bonus rows and group / market headers. */
export const BULLION_HEADER_GRADIENT =
    "linear-gradient(91.27deg, #F9FBC7 4.89%, #E6C878 12.85%, #FBF0A8 21.75%, #7B5E2C 67.65%, #AE8E53 78.42%, #AE8E53 86.85%, #7C582D 102.3%)";

/** Live ball strip — OVER tab above the white footer bar. */
export const BULLION_LIVE_OVER_TAB_GRADIENT =
    "linear-gradient(99.27deg, #F9FBC7 4.3%, #E6C878 14.91%, #FBF0A8 26.78%, #7B5E2C 87.98%, #AE8E53 102.34%, #AE8E53 113.58%, #7C582D 134.19%)";
export const BULLION_LIVE_OVER_TAB_LABEL_CLASS =
    "font-anton text-[12px] font-normal not-italic uppercase leading-[100%] tracking-[0%] text-[#1B062F] [leading-trim:none]";

/** Leaderboard column header (POS. / PLAYER / POT. / POINTS). */
export const BULLION_LEADERBOARD_HEADER_STYLE: React.CSSProperties = {
    background: BULLION_HEADER_GRADIENT,
};
/** Gold leaderboard header row — fixed 30px height. */
export const BULLION_LEADERBOARD_HEADER_HEIGHT_CLASS =
    "box-border h-[30px] min-h-[30px] max-h-[30px] shrink-0 overflow-hidden";
export const BULLION_LEADERBOARD_HEADER_ROW_CLASS = `relative grid ${BULLION_LEADERBOARD_HEADER_HEIGHT_CLASS} ${BULLION_LEADERBOARD_GRID_COLS_CLASS} items-center border-b border-[#AE8E53]/35 pl-2 pr-0 text-[#1B062F]`;

/** In-play played-view leaderboard column header — purple, no gold shimmer. */
export const BULLION_LEADERBOARD_PLAYED_HEADER_STYLE: React.CSSProperties = {
    ...BULLION_SLIP_MARKET_PANEL_BG_STYLE,
};
export const BULLION_LEADERBOARD_PLAYED_HEADER_ROW_CLASS = `relative grid ${BULLION_LEADERBOARD_HEADER_HEIGHT_CLASS} ${BULLION_LEADERBOARD_GRID_COLS_CLASS} items-center border-b border-white/10 pl-2 pr-0 text-white`;

/** My picks / slip group tab headers — golden shimmer bar. */
export const BULLION_GROUP_SECTION_HEADER_CLASS =
    "bullion-header-shimmer flex h-[30px] items-center px-2 !text-[#2a1604]";

export const BULLION_GROUP_SECTION_HEADER_STYLE: React.CSSProperties = {
    background: BULLION_HEADER_GRADIENT,
};

/** Shared desktop inset for Bullion match chrome, team strip, and content columns (margin-style, not padding on a wrapper). */
export { MATCH_PLAY_DESKTOP_PAGE_GUTTER_CLASS as BULLION_DESKTOP_PAGE_GUTTER_CLASS } from "../../Matches/matchDetailLayout";

/** Desktop room board page fill — bottom-left #1B062F → top-right #3d1869. */
export const BULLION_ROOM_BOARD_DESKTOP_PAGE_BG_CLASS =
    "md:bg-[linear-gradient(to_top_right,#1B062F_0%,#3d1869_100%)]";

export const BULLION_BONUS_ROW_MARKET_PANEL_BG_STYLE: React.CSSProperties = {
    background: BULLION_HEADER_GRADIENT,
};

/** Dark scrim on bonus-row POT / bonus cells (board pick column + My picks PTS column). */
export const BULLION_BONUS_ROW_POT_PANEL_BG = "rgba(27, 6, 47, 0.2)";

/** Handicap line numbers on golden bonus rows — lime for contrast on gold. */
export const BULLION_GOLDEN_ROW_NUMERIC_CLASS = "font-bold tabular-nums text-lime";

/** Midpoint of the market gradient at 10% opacity — `PlayerHeadshotImage` only supports solid `backgroundColor`. */
export const BULLION_SLIP_PLAYER_HEADSHOT_BACKDROP = (() => {
    const a = bullionSlipParseHexRgb(BULLION_SLIP_MARKET_GRADIENT_FROM);
    const b = bullionSlipParseHexRgb(BULLION_SLIP_MARKET_GRADIENT_TO);
    const r = Math.round((a.r + b.r) / 2);
    const g = Math.round((a.g + b.g) / 2);
    const bl = Math.round((a.b + b.b) / 2);
    return `rgba(${r}, ${g}, ${bl}, 0.1)`;
})();

/** `PlayerHeadshotImage` supports `teamName` as string or fallback array; `.jsx` typings are string-only. */
export const BullionSlipPlayerImage = PlayerHeadshotImage as React.ComponentType<{
    teamName?: string | string[];
    playerName?: string;
    imageUrl?: string;
    width?: string;
    height?: string;
    /** Tile behind headshot (Bullion My Picks modal); pairs with `imgMixBlendMode` for white roster mats. */
    backdropColor?: string;
    imgMixBlendMode?: string;
}>;

export const EVENT_STATUS_DISPLAY: Record<number, { text: string }> = {
    [EVENT_STATUS.MATCH_SCHEDULED]: { text: "SCHEDULED" },
    [EVENT_STATUS.MATCH_STABLE]: { text: "IN-PLAY" },
    [EVENT_STATUS.BALL_IN_PROGRESS]: { text: "IN-PLAY" },
    [EVENT_STATUS.UNKNOWN_EVENT]: { text: "IN-PLAY" },
    [EVENT_STATUS.RUNS_LIKELY]: { text: "IN-PLAY" },
    [EVENT_STATUS.POSSIBLE_WICKET]: { text: "IN-PLAY" },
    [EVENT_STATUS.UMPIRE_REVIEW]: { text: "IN-PLAY" },
    [EVENT_STATUS.BREAK_IN_PLAY]: { text: "BREAK IN PLAY" },
    [EVENT_STATUS.DATA_ASSEMBLY_ISSUE]: { text: "IN-PLAY" },
    [EVENT_STATUS.SUPER_OVER]: { text: "IN-PLAY" },
    [EVENT_STATUS.MATCH_COMPLETE]: { text: "FINISHED" },
    [EVENT_STATUS.EVENT_CLOSED]: { text: "FINISHED" },
    [EVENT_STATUS.EJECTED]: { text: "FINISHED" },
};

/** Same as Trader match-play / results detail — `/completed` can land slightly after mount. */
export const BULLION_COMPLETED_DATA_RETRY_MS = 2000;
/** Match landing top chrome — 34px bar (same bar as `BullionMatchLanding`). */
export const BULLION_MATCH_CHROME_HEADER_BAR_CLASS = "bg-[#652CAF]";

/** Match chrome: back row nav label (Anton 12 / 100% lh / uppercase) — e.g. LOBBY, BULLION HOME. */
export const BULLION_MATCH_CHROME_LOBBY_LABEL_CLASS =
    "font-anton text-[12px] font-normal not-italic uppercase leading-[100%] tracking-[0%] [leading-trim:none] text-white";

/** Contest subheader — arena / battle name (lime). */
export const BULLION_MATCH_CHROME_CONTEST_ARENA_CLASS =
    "min-w-0 truncate font-anton text-[12px] font-normal not-italic uppercase leading-[100%] tracking-[0%] [leading-trim:none] text-[#D2FF00]";

/** Competition label (right, #FFFFFF @ 50% opacity). */
export const BULLION_MATCH_CHROME_COMPETITION_CLASS =
    "min-w-0 max-w-[48%] shrink-0 truncate text-right font-anton text-[12px] font-normal not-italic uppercase leading-[100%] tracking-[0%] [leading-trim:none] text-[#FFFFFF] opacity-50";

/** How long the settled-win celebration (mobile bottom reveal / desktop row highlight) stays before auto-dismiss. */
export const BULLION_WIN_TOAST_DISMISS_MS = 6000;
/** Persist which slip win rows already showed the WIN toast (per game), so revisits don’t replay old wins but missed wins still toast once. */
export const BULLION_WIN_TOAST_ACK_PREFIX = "bullion_win_toast_ack:";

export function bullionWinToastReadAckIds(gameId: number | string | null | undefined): Set<string> {
    if (gameId == null || typeof window === "undefined") return new Set();
    try {
        const raw = window.localStorage.getItem(`${BULLION_WIN_TOAST_ACK_PREFIX}${String(gameId)}`);
        if (!raw) return new Set();
        const parsed = JSON.parse(raw) as unknown;
        if (!Array.isArray(parsed)) return new Set();
        return new Set(parsed.filter((x): x is string => typeof x === "string"));
    } catch {
        return new Set();
    }
}

export function bullionWinToastPersistAckIds(gameId: number | string, rowIds: readonly string[]): void {
    if (typeof window === "undefined" || rowIds.length === 0) return;
    try {
        const merged = bullionWinToastReadAckIds(gameId);
        for (const id of rowIds) merged.add(id);
        window.localStorage.setItem(`${BULLION_WIN_TOAST_ACK_PREFIX}${String(gameId)}`, JSON.stringify([...merged]));
    } catch {
        /* quota / private mode */
    }
}
/** Played-view mobile footer — POT label + amount (Anton 12 / 12px lh, white uppercase). */
export const BULLION_PLAYED_FOOTER_PRIZE_POT_LABEL_CLASS =
    "font-anton text-[12px] font-normal not-italic uppercase leading-[12px] tracking-[0%] text-white [leading-trim:none]";
export const BULLION_PLAYED_FOOTER_PRIZE_POT_ROW_CLASS =
    "inline-flex min-w-0 shrink-0 items-center gap-1";
/** Coin + amount sit tight together; label keeps a small gap before the coin. */
export const BULLION_PLAYED_FOOTER_PRIZE_POT_VALUE_CLASS = "inline-flex items-center gap-0.5";

/** Currency coin in played footer / prize pot rows — 20×20 with soft drop shadow. */
export const BULLION_CURRENCY_COIN_SHADOW_STYLE: React.CSSProperties = {
    boxShadow: "0px 2px 3px 0px #00000026",
};

/** Footer strip: LEADERBOARD / MY PICKS / HOW TO PLAY — Anton 400, 12px / 20px lh, 0% tracking, uppercase, 30px height. */
export const bullionPlayedFooterStripNavTypo =
    "font-anton text-[12px] font-normal not-italic uppercase leading-[20px] tracking-[0%] [leading-trim:none]";
export const bullionPlayedFooterStripNavHeight = "box-border flex h-[30px] min-h-[30px] max-h-[30px] shrink-0 items-center justify-center py-0";
